import React from "react";
import type { MemberAttribute } from "../../../../shared/types/member.types";
import { MemberAttributeProvider } from "../../providers/MemberAttributeContext";
import { MultiSelectFilter } from "./filters/MultiSelectFilter";
import { RangedDateFilter } from "./filters/RangedDateFilter";
import { RangedNumberFilter } from "./filters/RangedNumberFilter";
import { SimpleFilterInput } from "./filters/SimpleFilterInput";
import { SingleExistenceSelectFilter } from "./filters/SingleExistenceSelectFilter";
import type { MemberAttributeFilterConfig } from "./types";

export const MemberAttributeFilters: React.FC<{
	memberAttributes: MemberAttribute[];
	config?: MemberAttributeFilterConfig;
}> = ({
	memberAttributes,
	config = {
		layout: "grid",
		showHelpText: true,
		enableAnimation: false,
		placeholderPattern: "input",
	},
}) => {
	const filteredAttributes = React.useMemo(() => {
		const seenSections = new Set<string>();
		return memberAttributes.filter((attr) => {
			if (attr.section && attr.type.startsWith("address")) {
				if (seenSections.has(attr.section)) return false;
				seenSections.add(attr.section);
				return true;
			}
			return true;
		});
	}, [memberAttributes]);

	const simpleTextTypes =
		config.layout === "vertical"
			? [
					"text",
					"address",
					"textarea",
					"color",
					"url",
					"email",
					"telephone",
					"address_postal",
					"address_prefecture",
					"address_city",
					"address_address",
					"address_building",
					"firstName",
					"lastName",
					"firstNameKana",
					"lastNameKana",
				]
			: [
					"text",
					"textarea",
					"color",
					"url",
					"email",
					"telephone",
					"address",
					"address_postal",
					"address_prefecture",
					"address_city",
					"address_address",
					"address_building",
					"firstName",
					"lastName",
					"firstNameKana",
					"lastNameKana",
				];

	const rangedNumberTypes =
		config.layout === "vertical"
			? ["number", "number_integer", "number_float"]
			: ["number_integer", "number_float"];

	const rangedDateTypes =
		config.layout === "vertical"
			? ["datepicker", "date", "time", "datetime"]
			: ["date", "time", "datetime"];

	const content = (
		<>
			{filteredAttributes.map((memberAttribute: MemberAttribute) => (
				<MemberAttributeProvider
					key={`MemberAttributeFilters-${memberAttribute.memberAttributeId}`}
					memberAttribute={memberAttribute}
					config={config}
				>
					{memberAttribute.type === "image" && <SingleExistenceSelectFilter />}

					{simpleTextTypes.includes(memberAttribute.type) && (
						<SimpleFilterInput />
					)}

					{rangedNumberTypes.includes(memberAttribute.type) && (
						<RangedNumberFilter />
					)}

					{rangedDateTypes.includes(memberAttribute.type) && (
						<RangedDateFilter />
					)}

					{["checkbox", "radio", "select"].includes(memberAttribute.type) && (
						<MultiSelectFilter />
					)}
				</MemberAttributeProvider>
			))}
		</>
	);

	return <>{content}</>;
};

export type { MemberAttributeFilterConfig } from "./types";
export default MemberAttributeFilters;
